import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Grid
} from '@material-ui/core';
import { companyTypes } from './companyTypes';
import useVrackApi from '../../hooks/useVrackApi';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';

EditCompanyDialog.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  // No-arg callback invoked after a successful update — typically the
  // parent's `refetch` for the companies list.
  onUpdated: PropTypes.func,
  company: PropTypes.object
};

EditCompanyDialog.defaultProps = {
  company: null
};

export default function EditCompanyDialog({ isOpen, closeHandler, onUpdated, company }) {
  const [name, setName] = useState('');
  const [type, setType] = useState(companyTypes[0]);
  const [contactEmail, setContactEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: updateCompanyApi } = useVrackApi('put', '/admin/companies');

  useEffect(() => {
    if (company) {
      setName(company.name || '');
      setType(company.type || companyTypes[0]);
      setContactEmail(company.contactEmail || '');
      setNameError('');
      setLoading(false);
    }
  }, [company]);

  const validateName = (value) => {
    if (!value.trim()) {
      setNameError('Enter a company name.');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleUpdate = async () => {
    const nameValid = validateName(name);
    if (!nameValid) return;

    setLoading(true);
    try {
      await updateCompanyApi({
        data: {
          company_id: company.id,
          name: name.trim(),
          type,
          contact_email: contactEmail.trim()
        }
      });
      openSnackbar({ message: `${name.trim()} updated successfully.` });
      if (onUpdated) onUpdated();
      handleReset();
    } catch (error) {
      openSnackbar({ message: 'Failed to update company.', severity: 'error' });
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (company) {
      setName(company.name || '');
      setType(company.type || companyTypes[0]);
      setContactEmail(company.contactEmail || '');
    }
    setNameError('');
    setLoading(false);
    closeHandler();
  };

  const handleCancel = () => {
    if (loading) return;
    handleReset();
  };

  if (!company) return null;

  return (
    <Dialog open={isOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Company</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Update the company record. The Company ID is fixed and cannot be changed.
        </DialogContentText>
        <Grid container item spacing={1} mt={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              required
              label="Company name"
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                validateName(e.target.value);
              }}
              error={!!nameError}
              helperText={nameError || ' '}
              placeholder="Example: United Airlines"
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company ID"
              fullWidth
              variant="outlined"
              value={company.id}
              disabled
              helperText=" "
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Company type"
              fullWidth
              variant="outlined"
              value={type}
              onChange={(e) => setType(e.target.value)}
              helperText=" "
              disabled={loading}
            >
              {companyTypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              type="email"
              label="Company contact email"
              fullWidth
              variant="outlined"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="admin@panasonic.aero"
              helperText=" "
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpdate} disabled={loading}>
          Update Company
        </Button>
      </DialogActions>
    </Dialog>
  );
}
